import { designTokens } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Dropdown } from "@itandi/itandi-bb-ui/Dropdown";
import { DropdownMenu } from "@itandi/itandi-bb-ui/DropdownMenu";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useSWRMutation from "swr/mutation";
import { TodoTemplateModal } from "~/components/common/todo_templates/TodoTemplateModal";
import { useTodoTemplates } from "~/hooks/useTodoTemplates";
import type { DealType } from "~/models/Deal";
import type { TodoTemplate } from "~/models/TodoTemplate";
import { filterTodoTemplatesByDealType } from "~/models/TodoTemplate";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy } from "~/utils/api/TodoTemplate";
import { generatePath } from "~/utils/generatePath";

type Props = {
	dealType: DealType;
	subtypeId: number | null;
};

export function TodoTemplateList({ dealType, subtypeId }: Props) {
	const { data: todoTemplates, isLoading } = useTodoTemplates().useIndex();
	const [editingTemplate, setEditingTemplate] = useState<TodoTemplate | null>(
		null
	);
	const [deletingTemplate, setDeletingTemplate] = useState<TodoTemplate | null>(
		null
	);

	const filteredTodoTemplates = useMemo(() => {
		const templates = filterTodoTemplatesByDealType(
			dealType,
			subtypeId,
			todoTemplates
		);

		// ステータス順に並び替える
		return templates.sort((a, b) => a.statusId - b.statusId);
	}, [dealType, subtypeId, todoTemplates]);

	const { trigger: triggerDestroy, isMutating } = useSWRMutation(
		generatePath("/todo_templates"),
		async (_key, { arg }: { arg: number }) => destroy(arg)
	);

	const onDelete = useCallback(async () => {
		const result = await triggerDestroy(deletingTemplate!.id);
		if (isSuccess(result)) {
			setDeletingTemplate(null);
			toast.success("削除しました。");
		} else {
			toast.error("削除に失敗しました。");
		}
	}, [triggerDestroy, deletingTemplate]);

	return (
		<>
			<Table>
				<Thead>
					<Tr>
						<Th width="13rem">作成ステータス</Th>
						<Th>タイトル</Th>
						<Th width="5rem">操作</Th>
					</Tr>
				</Thead>
				<Tbody loading={isLoading}>
					{filteredTodoTemplates.map((todoTemplate) => (
						<Tr key={todoTemplate.id}>
							<Td>{todoTemplate.statusName}</Td>
							<Td>{todoTemplate.title}</Td>
							<Td>
								<Dropdown
									button={
										<Button height="small" type="button" variant="secondary">
											操作
										</Button>
									}
								>
									<DropdownMenu
										onClick={() => {
											setEditingTemplate(todoTemplate);
										}}
									>
										編集
									</DropdownMenu>
									<DropdownMenu
										danger
										onClick={() => {
											setDeletingTemplate(todoTemplate);
										}}
									>
										削除
									</DropdownMenu>
								</Dropdown>
							</Td>
						</Tr>
					))}
				</Tbody>
			</Table>
			{editingTemplate !== null ? (
				<TodoTemplateModal
					dealType={dealType}
					handleClose={() => {
						setEditingTemplate(null);
					}}
					subtypeId={subtypeId}
					todoTemplate={editingTemplate}
				/>
			) : null}
			{deletingTemplate !== null ? (
				<Modalv2
					isOpen
					onClose={() => {
						setDeletingTemplate(null);
					}}
					size="s"
				>
					<ModalHeader title="TODOテンプレートの削除" />
					<ModalBody>
						TODOテンプレートを削除します。
						<Alertv2 mt={designTokens["spacing/m"]} variant="warning">
							<Paragraph>この操作は元に戻せません。</Paragraph>
						</Alertv2>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Button
								disabled={isMutating}
								onClick={() => {
									setDeletingTemplate(null);
								}}
								variant="secondary"
							>
								キャンセル
							</Button>
						}
						rightArea={
							<Button loading={isMutating} onClick={onDelete} variant="danger">
								削除
							</Button>
						}
					/>
				</Modalv2>
			) : null}
		</>
	);
}
